import {PrismicNextImage} from '@prismicio/next'
import {PrismicRichText} from '@prismicio/react'
import {useLinkResolver} from 'prismicio'
import {CtaV2SliceContainedImage} from 'prismicio-types'
import {maskStyles} from 'slices/AnimatedList/constants'
import {ButtonV2} from 'src/common/ButtonV2'
import {Container} from 'src/common/Container'
import {getButtonsData} from 'src/common/helpers'
import {Icon} from 'src/common/Icon'

type ContainedImageProps = {
  slice: CtaV2SliceContainedImage & {
    slice_type: string
    variation: string
  }
  index?: number
  context?: {
    pageUid?: string
    buttonsData?: any
  }
}

export const ContainedImageVariation = ({slice, index, context}: ContainedImageProps) => {
  const {primaryButtonData, secondaryButtonData} = getButtonsData({
    primaryButton: slice.primary.primary_button,
    secondaryButton: slice.primary.secondary_button,
    buttonsData: context?.buttonsData,
  })

  const {linkResolver} = useLinkResolver()

  return (
    <Container className="items-center justify-center !py-10 px-5 md:px-10 lg:!py-20 lg:px-5 xl:px-0">
      <div className="flex w-full max-w-[1200px] flex-col gap-12 rounded-lg bg-green-dark p-6 md:flex-row lg:gap-6 lg:p-8">
        <div className="flex flex-col gap-6 md:justify-between lg:w-full">
          <div className="flex flex-col gap-4">
            <p className="text-button-s text-green-neon">{slice.primary.tag}</p>
            <PrismicRichText
              field={slice.primary.title}
              components={{
                heading2: ({children}) => (
                  <h2 className="lg:text-tg-h2 text-32 font-normal leading-[normal] tracking-[-.32px] text-moss-white">
                    {children}
                  </h2>
                ),
                strong: ({children}) => <span className="text-moss-white/[70%]">{children}</span>,
                em: () => <br className="max-lg:hidden" />,
              }}
            />
          </div>
          {primaryButtonData && (
            <ButtonV2
              trackingData={{
                mktgId: primaryButtonData.marketing_id || 'cta-button-primary',
                location: `cta-${slice.variation}-${index}`,
                pageName: context?.pageUid,
                dataType: 'cta-button',
              }}
              variant="primary"
              className="!w-fit"
              href={linkResolver(primaryButtonData.link)}
            >
              {primaryButtonData.label}
            </ButtonV2>
          )}
          {secondaryButtonData && (
            <ButtonV2
              trackingData={{
                mktgId: secondaryButtonData.marketing_id || 'cta-button-secondary',
                location: `cta-${slice.variation}-${index}`,
                pageName: context?.pageUid,
                dataType: 'cta-button',
              }}
              variant="secondary"
              className="!w-fit"
              href={linkResolver(secondaryButtonData.link)}
            >
              {secondaryButtonData.label}
            </ButtonV2>
          )}
        </div>
        <div className="relative isolate md:w-full md:max-w-[556px]">
          <PrismicNextImage
            field={slice.primary.image.mobile}
            className="aspect-square w-full max-w-[556px] rounded-[6px] object-cover lg:hidden"
            quality={100}
          />
          <PrismicNextImage
            field={slice.primary.image}
            className="aspect-[139/80] w-full max-w-[556px] rounded-[6px] object-cover max-lg:hidden"
            quality={100}
          />
          <div className="absolute inset-0 flex items-end justify-start p-4">
            <div className="flex w-full items-center gap-4 rounded-[4px] bg-moss-white/[10%] p-3 backdrop-blur-[40px]">
              <div className="relative flex h-8 min-h-8 w-8 min-w-8 items-center justify-center self-start rounded-[4px] bg-moss-white">
                <Icon name="quotation-mark" className="h-5 w-5 min-w-5 text-[#1E1D1D]" />
              </div>
              <span className="text-15 font-medium leading-[19px] text-moss-white">{slice.primary.quote[0]?.text}</span>
              <div className="pointer-events-none absolute inset-0 rounded-md" style={maskStyles} />
            </div>
          </div>
        </div>
      </div>
    </Container>
  )
}
